/**
 * The topbar user menu (design/ux/01_foundations.md §6.2; AUD-NAV-04,
 * AUD-NAV-22, AUD-NAV-03; WP-18).
 *
 * The APG menu-button pattern through the kit's one MenuButton
 * (components/ui/Menu.tsx): role="menu" / menuitem, ↑/↓ Début/Fin, letters,
 * Entrée activates, Échap closes and returns focus to the button, 40 px items
 * at 16 px. Entries: Mon compte · Paramètres (only where ROUTE_ACCESS admits
 * the role — PROPRIETAIRE) · Préférences d'affichage · Raccourcis clavier (F1)
 * · Support · À propos (version) · Se déconnecter. Showcase is gone (A-20).
 * X XWP-72 — a user picked by PIN on a team workstation is offered « Changer d'utilisateur » in place
 * of « Se déconnecter » (the workstation stays open; the picker comes back), and the header names the
 * workstation.
 */
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Button, Icon, Modal } from "@/components/ui/primitives";
import { MenuButton, type MenuItem } from "@/components/ui/Menu";
import { RoleBadge } from "@/components/domain/Domain";
import { LOCALE_LABELS } from "@/config/locale.config.js";
import { APP_VERSION } from "@/config/app.config.js";
import { canAccessRoute } from "@/config/routeAccess.config.js";
import { useAuthStore } from "@/stores/authStore.js";
import { useThemeStore } from "@/stores/themeStore.js";
import { useKeyboardStore } from "@/stores/keyboardStore.js";
import { MESSAGES_SESSION_EQUIPE } from "@/config/session-equipe.config.js";
import { estSelectionEquipe, lirePoste } from "@/lib/session-equipe.js";

/** The entries a role is offered (pure: unit-tested). */
export function userMenuEntries(
  role: string | null | undefined,
  actions: {
    go: (to: string) => void;
    preferences: () => void;
    shortcuts: () => void;
    about: () => void;
    logout: () => void;
    changerUtilisateur?: () => void;
  },
  options: { sessionEquipe?: boolean } = {},
): MenuItem[] {
  const items: MenuItem[] = [];
  const route = (id: string, label: string, icon: string, to: string) => {
    if (canAccessRoute(role, to)) items.push({ id, label, icon, onSelect: () => actions.go(to) });
  };
  route("compte", "Mon compte", "user", "/moncompte");
  route("parametres", "Paramètres", "settings", "/parametres");
  items.push({ id: "affichage", label: "Préférences d'affichage", icon: "sliders-horizontal", onSelect: actions.preferences });
  items.push({ id: "raccourcis", label: "Raccourcis clavier", icon: "keyboard", shortcut: "F1", onSelect: actions.shortcuts });
  route("support", "Support", "life-buoy", "/support");
  items.push({ id: "apropos", label: "À propos", icon: "info", hint: `Version ${APP_VERSION}`, onSelect: actions.about });
  if (options.sessionEquipe) {
    items.push({
      id: "changer",
      label: MESSAGES_SESSION_EQUIPE.menu.changer,
      icon: "users",
      separatorBefore: true,
      onSelect: actions.changerUtilisateur ?? actions.logout,
    });
    return items;
  }
  items.push({
    id: "deconnexion",
    label: "Se déconnecter",
    icon: "log-out",
    danger: true,
    separatorBefore: true,
    onSelect: actions.logout,
  });
  return items;
}

const initialsOf = (user: any) =>
  user?.avatar_initials || `${user?.prenom?.[0] ?? ""}${user?.nom?.[0] ?? ""}`.toUpperCase() || "?";

export const UserMenu: React.FC = () => {
  const user = useAuthStore((s: any) => s.user);
  const logout = useAuthStore((s: any) => s.logout);
  const changerUtilisateur = useAuthStore((s: any) => s.changerUtilisateur);
  const setShowTweaks = useThemeStore((s: any) => s.setShowTweaks);
  const openHelp = useKeyboardStore((s) => s.openHelp);
  const navigate = useNavigate();
  const [about, setAbout] = React.useState(false);
  const role: string | null = user?.role ?? null;
  const roleLabel = role ? ((LOCALE_LABELS as Record<string, string>)[role] ?? role) : "";
  const fullName = [user?.prenom, user?.nom].filter(Boolean).join(" ");
  // read on every render: the marker changes with the user (a switch reloads the page anyway)
  const sessionEquipe = estSelectionEquipe();
  const libellePoste: string | null = sessionEquipe ? (lirePoste()?.session?.libelle_poste ?? null) : null;

  const items = userMenuEntries(
    role,
    {
      go: (to) => navigate(to),
      preferences: () => setShowTweaks?.(true),
      shortcuts: () => openHelp(),
      about: () => setAbout(true),
      logout: () => logout?.(),
      changerUtilisateur: () => changerUtilisateur?.(),
    },
    { sessionEquipe },
  );

  return (
    <>
      <MenuButton
        label={`Menu utilisateur — ${fullName || "compte"}`}
        align="end"
        items={items}
        className="pk-topbar-user"
        menuClassName="pk-user-menu"
        triggerContent={
          <>
            <span className="pk-topbar-avatar" aria-hidden="true">
              {initialsOf(user)}
            </span>
            <span className="pk-topbar-user-text">
              <span className="pk-topbar-user-name">{fullName}</span>
              <span className="pk-topbar-user-role">{roleLabel}</span>
            </span>
            <Icon name="chevron-down" size={16} className="pk-topbar-user-chevron" />
          </>
        }
        header={
          <div className="pk-user-menu-id">
            <span className="pk-user-menu-name">{fullName}</span>
            {user?.email && <span className="pk-user-menu-email">{user.email}</span>}
            {role && <RoleBadge role={role} size="sm" />}
            {libellePoste && <span className="pk-user-menu-email">{MESSAGES_SESSION_EQUIPE.menu.poste(libellePoste)}</span>}
          </div>
        }
      />
      <Modal
        open={about}
        onClose={() => setAbout(false)}
        title="À propos de Pharmakon"
        size="sm"
        dirty={false}
        footer={
          <Button variant="primary" onClick={() => setAbout(false)}>
            Fermer
          </Button>
        }
      >
        <dl className="pk-about">
          <dt>Application</dt>
          <dd>Pharmakon — système de gestion pharmaceutique</dd>
          <dt>Version</dt>
          <dd className="font-mono">{APP_VERSION}</dd>
          {user?.pharmacie?.nom && (
            <>
              <dt>Pharmacie</dt>
              <dd>{user.pharmacie.nom}</dd>
            </>
          )}
          {roleLabel && (
            <>
              <dt>Connecté en tant que</dt>
              <dd>{`${fullName}${fullName ? " · " : ""}${roleLabel}`}</dd>
            </>
          )}
        </dl>
      </Modal>
    </>
  );
};

export default UserMenu;
